import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { range } from "lodash";

import { Message } from "lib/components/vendor";
import { MainView, SubBar, Button } from "lib/index";
import { getDateRange } from "lib/utils/date";
import st from "lib/utils";
import { Link } from "lib/components";
import ProductScheduleSlotPricingsRepository from "lib/repositories/ProductScheduleSlotPricingsRepository";
import { pushCompanyRoute } from "modules/app/actions";
import AccommodationSchedulerForm from "./AccommodationSchedulerForm";
import { stateSelector, initializeView, fetchProducts, fetchPricingPlans } from "../common";

@connect(
  (state) => ({
    currentCompanyId: state.auth.company.model.id,
    products:
      stateSelector(state).products &&
      stateSelector(state).products.model &&
      stateSelector(state).products.model.records,
    pricingPlans: stateSelector(state).pricingPlans && stateSelector(state).pricingPlans.model,
    fetchDone:
      stateSelector(state).products &&
      stateSelector(state).products.fetchDone &&
      stateSelector(state).pricingPlans &&
      stateSelector(state).pricingPlans.fetchDone,
    fetchLoading:
      (stateSelector(state).products && stateSelector(state).products.fetchLoading) ||
      (stateSelector(state).pricingPlans && stateSelector(state).pricingPlans.fetchLoading),
    fetchFailed:
      (stateSelector(state).products && stateSelector(state).products.fetchFailed) ||
      (stateSelector(state).pricingPlans && stateSelector(state).pricingPlans.fetchFailed),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        initializeView,
        fetchProducts,
        fetchPricingPlans,
        pushCompanyRoute,
      },
      dispatch
    )
)
export default class AccommodationSchedulerView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      schedule: {
        productVariants: [],
        pricingPlans: [],
        dates: {
          from: null,
          to: null,
          days: range(0, 7),
        },
        availabilityType: null,
        totalStockLeft: null,
        minNightStay: null,
      },
      submitLoading: false,
      errors: new st.form.ValidationErrors(),
      responseError: {
        message: null,
        startDate: null,
        endDate: null,
      },
      loadingIndex: 0,
      loadingStart: null,
      loadingEnd: null,
      numberOfRequests: null,
    };
    this.submit$ = null;
  }

  componentDidMount() {
    this.props.initializeView();
  }

  componentWillUnmount() {
    if (this.submit$) {
      this.submit$.unsubscribe();
    }
  }

  retryFetching() {
    const { fetchProducts, fetchPricingPlans } = this.props;
    fetchProducts();
    fetchPricingPlans();
  }

  handleScheduleChange(schedule) {
    this.setState({ schedule });
  }

  handleSave() {
    const { schedule } = this.state;
    const dateRange = getDateRange(schedule.dates.from, schedule.dates.to);
    const possibleDates = dateRange.filter((date) => schedule.dates.days.includes(moment(date).weekday()));

    if (!this.validate(possibleDates)) {
      return;
    }
    const { slots, pricings } = this.prepareScheduleData(possibleDates);
    this.processRequest(slots, pricings);
  }

  validate(possibleDates) {
    const { errors, schedule } = this.state;
    errors.clear();
    this.setState({
      errors,
      responseError: {
        message: null,
        startDate: null,
        endDate: null,
      },
    });

    errors.add("accommodation.schedule.productVariants", st.validators.presence(schedule.productVariants));
    errors.add("accommodation.schedule.dateRange", st.validators.presence(schedule.dates.from));
    errors.add("accommodation.schedule.dateRange", st.validators.presence(schedule.dates.to));
    errors.add("accommodation.schedule.days", st.validators.presence(schedule.dates.days));
    errors.add(
      "accommodation.schedule.days",
      schedule.dates.from && schedule.dates.to && !possibleDates.length
        ? I18n.t("papp.accommodation.AccommodationSchedulerView.errors.daysNotIncluded")
        : undefined
    );

    this.setState({ errors });
    return errors.valid;
  }

  prepareScheduleData(possibleDates) {
    const { currentCompanyId } = this.props;
    const { schedule } = this.state;
    const productIdsByProductVariantIds = this.getProductIdsByProductVariantIds();
    const slots = [];
    const pricings = [];

    possibleDates.forEach((date) => {
      const formattedStartDate = moment(date).format("DD-MM-YYYY");
      const formattedEndDate = moment(date).add(1, "day").format("DD-MM-YYYY");

      schedule.productVariants.forEach((pvId) => {
        slots.push({
          starts_at: formattedStartDate,
          ends_at: formattedEndDate,
          company_id: currentCompanyId,
          product_id: productIdsByProductVariantIds[pvId],
          product_variant_id: pvId,
          ...(schedule.availabilityType !== null ? { availability_type: schedule.availabilityType } : {}),
          ...(schedule.totalStockLeft !== null ? { total_stock_left: schedule.totalStockLeft } : {}),
          ...(schedule.minNightStay !== null ? { min_night_stay: schedule.minNightStay } : {}),
        });

        if (schedule.pricingPlans.length > 0) {
          schedule.pricingPlans.forEach((pricingPlan) => {
            pricings.push({
              date: formattedStartDate,
              product_variant_id: pvId,
              pricing_plan_id: pricingPlan.id,
              price_cents: pricingPlan.amount * 100,
            });
          });
        }
      });
    });

    return { slots, pricings };
  }

  processRequest(slots, pricings) {
    if (this.submit$) {
      this.submit$.unsubscribe();
    }


    const isSlotsAndPricingsEqual = slots.length === pricings.length;
    const maxSlotsPerRequest = 130;
    const maxPricingsPerRequest = isSlotsAndPricingsEqual ? 130 : 260;

    const chunkedSlots = this.chunkArray(slots, maxSlotsPerRequest);
    const chunkedPricings = this.chunkArray(pricings, maxPricingsPerRequest);

    const slotsRequests = Math.ceil(slots.length / maxSlotsPerRequest);
    const pricingRequests = Math.ceil(pricings.length / maxPricingsPerRequest);

    this.setState({
      submitLoading: true,
      loadingIndex: 0,
      loadingStart: null,
      loadingEnd: null,
      numberOfRequests: slotsRequests >= pricingRequests ? slotsRequests : pricingRequests,
      responseError: {
        message: null,
        startDate: null,
        endDate: null,
      },
    });

    this.sendNextRequest(chunkedSlots, chunkedPricings, 0);
  }

  sendNextRequest(chunkedSlots, chunkedPricings, index) {
    if (index >= chunkedSlots.length) {
      this.setState({ submitLoading: false }, () => {
        this.props.pushCompanyRoute(`/accommodation`);
      });
      return;
    }

    const batchSlots = chunkedSlots[index];
    const batchPricings = chunkedPricings[index];
    const startDate = batchSlots[0].starts_at;
    const endDate = batchSlots[batchSlots.length - 1].ends_at;

    this.setState({
      loadingIndex: index + 1,
      loadingStart: startDate,
      loadingEnd: endDate,
    });

    this.sendRequest(batchSlots, batchPricings)
      .then(() => {
        this.sendNextRequest(chunkedSlots, chunkedPricings, index + 1);
      })
      .catch((error) => {
        this.setState({
          submitLoading: false,
          responseError: {
            message: st.api.findResponseErrorMessage(error),
            startDate,
            endDate,
          },
          loadingIndex: 0,
          loadingStart: null,
          loadingEnd: null,
          numberOfRequests: null,
        });
      });
  }

  sendRequest(slots = [], pricings = []) {
    return new Promise((resolve, reject) => {
      const subscription = ProductScheduleSlotPricingsRepository.bulkUpdate(slots, pricings).subscribe(
        () => {
          subscription.unsubscribe();
          resolve();
        },
        (error) => {
          subscription.unsubscribe();
          reject(error);
        }
      );
    });
  }

  chunkArray(array, chunkSize) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  getProductIdsByProductVariantIds() {
    return this.props.products.reduce((obj, product) => {
      (product.product_variants || []).forEach((pv) => {
        obj[pv.id] = product.id;
      });
      return obj;
    }, {});
  }

  isScheduleDirty() {
    const {
      schedule: { productVariants, pricingPlans, dates, availabilityType, totalStockLeft, minNightStay },
    } = this.state;
    return (
      productVariants.length ||
      pricingPlans.length ||
      dates.from ||
      dates.to ||
      dates.days.length !== 7 ||
      availabilityType ||
      totalStockLeft !== null ||
      minNightStay !== null
    );
  }

  render() {
    const { currentCompanyId, products, pricingPlans, fetchDone, fetchLoading, fetchFailed, retryFetching } =
      this.props;
    const { schedule, submitLoading, errors, responseError, loadingStart, loadingEnd, numberOfRequests, loadingIndex } =
      this.state;

    const loadingMessage = `${loadingIndex}/${numberOfRequests}: ${loadingStart}-${loadingEnd}`;
    const responseErrorMessage =
      responseError.message !== null
        ? `${responseError.message} (${responseError.startDate} - ${responseError.endDate})`
        : null;

    return (
      <MainView.Container>
        <SubBar>
          <div className="u-displayFlex">
            <h1 className="u-flexGrow">{I18n.t("papp.accommodation.AccommodationSchedulerView.title")}</h1>
            <div>
              <Button
                as={Link}
                to={`/c/${currentCompanyId}/accommodation`}
                disabled={submitLoading}
                content={I18n.t("papp.accommodation.AccommodationSchedulerView.calendarLink")}
                className="u-marginRight"
              />
              <Button
                primary
                disabled={submitLoading || !this.isScheduleDirty()}
                onClick={this.handleSave.bind(this)}
                content={I18n.t("papp.common.save_changes")}
              />
            </div>
          </div>
        </SubBar>
        <MainView.Content hasSubBar>
          <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={retryFetching} />
          {fetchDone &&
            ((products || []).length ? (
              <AccommodationSchedulerForm
                products={products}
                pricingPlans={pricingPlans}
                schedule={schedule}
                onScheduleChange={this.handleScheduleChange.bind(this)}
                submitLoading={submitLoading}
                errors={errors}
                responseError={responseErrorMessage || responseError.message}
                loadingMessage={loadingMessage}
              />
            ) : (
              <Message warning compact>
                {I18n.t("papp.accommodation.AccommodationSchedulerView.noProductsInfo")}
              </Message>
            ))}
        </MainView.Content>
      </MainView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/accommodation/AccommodationSchedulerView/AccommodationSchedulerView.jsx